import React from 'react'
import { Button, Box, Icon, Text } from '@deseretbook/react-ui'
import PropTypes from 'prop-types'
import { AUDIOBOOK_CONTROL_VIEWS } from './AudiobookControls'
import ChapterListing from '../ChapterListing'
import MobileDrawer from '../../../mobileDrawer/MobileDrawer'

import './ControlView.css'
import { useDropdown } from '@deseretbook/react-utils'

/**
 * Audiobook control view.
 * @returns {function} Component
 */
export default function ControlView({
  isPlaying,
  isLoading,
  pause,
  play,
  next,
  duration,
  previous,
  rate,
  onChangeView,
  onPreviousTrack,
  onNextTrack,
  currentChapter,
  chapters,
  onSelectChapter,
  triggerProps,
  timeRemaining,
}) {
  const disabled = {
    disabled: duration === 0 || isLoading,
  }

  const chapterDropdown = useDropdown()

  /**
   * onClick handler.
   * @param {object} chapter - clicked chapter
   */
  const handleChapterSelect = (chapter) => {
    onSelectChapter(chapter)
    chapterDropdown.close()
  }

  const seekBackButton = (
    <Button
      id="control-button"
      {...disabled}
      onClick={previous}
      title={duration === 0 ? 'Please select a track to play' : 'Skip backwards 30 seconds'}
      aria-label={duration === 0 ? 'Please select a track to play' : 'Skip backwards 30 seconds'}
    >
      <Icon id="control-icon" name="seekBack" />
      <Text id="control-text">30</Text>
    </Button>
  )
  const playButton = (
    <Button
      id="control-button-play"
      isLoading={isLoading}
      {...disabled}
      isAction
      onClick={() => {
        return isPlaying ? pause() : play()
      }}
    >
      <Icon id="control-icon-play" name={isPlaying ? 'pause' : 'play'} />
    </Button>
  )
  const seekForwardButton = (
    <Button
      id="control-button"
      {...disabled}
      onClick={next}
      title={duration === 0 ? 'Please select a track to play' : 'Skip forward 30 seconds'}
      aria-label={duration === 0 ? 'Please select a track to play' : 'Skip forward 30 seconds'}
    >
      <Icon id="control-icon" name="seekForward" />
      <Text id="control-text">30</Text>
    </Button>
  )

  const playbackButton = (
    <Button
      id="control-option-button"
      {...disabled}
      onClick={() => onChangeView(AUDIOBOOK_CONTROL_VIEWS.playbackRate)}
      title={duration === 0 ? 'Please select a track to play' : 'Change volume'}
      aria-label={duration === 0 ? 'Please select a track to play' : 'Change volume'}
      {...triggerProps}
    >
      <Box isColumn alignItems="center" justifyContent="center">
        <Text
          style={{
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 700,
            fontSize: '16px',
            color: '#000000A6',
          }}
        >
          {rate}x
        </Text>
        <Text
          style={{
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 700,
            fontSize: '12px',
            lineHeight: '14px',
            color: '#000000A6',
          }}
        >
          Speed
        </Text>
      </Box>
    </Button>
  )

  const chaptersButton = (
    <Button
      id="control-option-button"
      title={duration === 0 ? 'Please select a track to play' : 'Change volume'}
      aria-label={duration === 0 ? 'Please select a track to play' : 'Change volume'}
      {...chapterDropdown.triggerProps}
    >
      <Box isColumn alignItems="center" justifyContent="center">
        <Icon id="control-option-icon" name="chapters" />
        <Text
          style={{
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 700,
            fontSize: '12px',
            lineHeight: '14px',
            color: '#000000A6',
          }}
        >
          Chapters
        </Text>
      </Box>
    </Button>
  )

  const skipForward = (
    <Button
      id="control-button"
      aria-label="Skip to next chapter"
      {...disabled}
      onClick={onNextTrack}
    >
      <Icon id="control-icon" name="skipForward" />
    </Button>
  )
  const skipBack = (
    <Button
      id="control-button"
      title="Skip to previous chapter"
      aria-label="Skip to previous chapter"
      {...disabled}
      onClick={onPreviousTrack}
    >
      <Icon id="control-icon" name="skipBack" />
    </Button>
  )

  return (
    <Box alignItems="center" isColumn style={{ width: '356px' }}>
      <Box alignItems="center" isFullwidth marginBottom={1} justifyContent="space-between">
        {skipBack}
        {seekBackButton}
        {playButton}
        {seekForwardButton}
        {skipForward}
      </Box>
      <Box alignItems="center" justifyContent="space-between" isFullwidth>
        {playbackButton}
        <Box isColumn alignItems="center">
          <Text>{timeRemaining.percentageCompleted}%</Text>
          <Text>{timeRemaining.readableTime} left</Text>
        </Box>
        {chaptersButton}
      </Box>
      <MobileDrawer {...chapterDropdown.dropdownProps} title="Table of Contents">
        <MobileDrawer.Content>
          <ChapterListing
            chapters={chapters}
            onSelectChapter={handleChapterSelect}
            currentChapter={currentChapter}
          />
        </MobileDrawer.Content>
      </MobileDrawer>
    </Box>
  )
}

ControlView.propTypes = {
  isPlaying: PropTypes.bool,
  isLoading: PropTypes.bool,
  pause: PropTypes.func,
  play: PropTypes.func,
  next: PropTypes.func,
  duration: PropTypes.number,
  rate: PropTypes.number,
  onChangeView: PropTypes.func,
  onPreviousTrack: PropTypes.func,
  onNextTrack: PropTypes.func,
  previous: PropTypes.func,
  currentChapter: PropTypes.shape({}),
  chapters: PropTypes.arrayOf(PropTypes.shape({})),
  onSelectChapter: PropTypes.func,
  timeRemaining: PropTypes.shape({}),
}

ControlView.defaultProps = {
  isPlaying: false,
  isLoading: false,
  pause: () => {},
  play: () => {},
  next: () => {},
  previous: () => {},
  onChangeView: () => {},
  onPreviousTrack: () => {},
  onNextTrack: () => {},
  duration: 0,
  rate: 1,
  currentChapter: {},
  chapters: [],
  onSelectChapter: () => {},
  timeRemaining: {},
}
